import {
  CancellationToken,
  putGcsBlob,
  UploadProgress,
} from "@kaggle/components";
import { featureFlags, Kaggle, LegacyDataSource, LegacyDataSourceReference } from "@kaggle/sdk";
import { getServices } from "../../../api/getServices";
import { draftSequenceMutex } from "../../editor/draft/makeSequentialSaveDraftWithQueue";
import { getAccelerator } from "../../redux/selectors/notebook";
import { watchRunForStatus } from "../../session/apiClient";
import { JupyterSource } from "../../session/JupyterSource";
import { Accelerator, isTpuAccelerator } from "../../types/Accelerator";
import { DockerImage } from "../../types/docker";
import { DockerImagePinningType } from "../../types/DockerImagePinningType";
import DraftSpec from "../../types/DraftSpec";
import KernelSpec from "../../types/KernelSpec";
import { KernelVersionType } from "../../types/KernelVersionType";
import { LanguageId } from "../../types/language";
import TerminalMessage from "../../types/TerminalMessage";
import { normalizeAmbiguousErrorMessages } from "../../utils/ambiguousErrors";
import { appendTerminalError } from "../actions/terminal";
import {
  getIsUtilityScript,
  UTILITY_SCRIPT_CATEGORY_ID,
} from "../selectors/category";
import {
  getCurrentDataSourceReferences,
  getCurrentDockerImageTag,
  getCurrentKernelRunId,
} from "../selectors/current";
import { getCurrentLanguageName } from "../selectors/kernelDraft";
import { getSourceType } from "../selectors/session";
import { getStore } from "../store/appStore";
import { AppAction } from "../types";
import { DataSourceExtended } from "../types/DataSourceExtended";
import { NotebookActionKey as ActionKey } from "./ActionKeys";
import {
  BatchedRunAction,
  removeBatchedRun,
  setBatchedRunCommitting,
  setBatchedRunErrorMessage,
  setBatchedRunUploading,
  setBatchedRunUploadingFiles,
} from "./batchedRun";
import { handleQuotaError } from "./loadKernel";
import { fetchRunLogs } from "./runs";
import { appendTerminal } from "./terminal";
import { restartWorker, updateWorker } from "./worker";

type Tag = Kaggle.Sdk.Tags.Protos.Tag;

interface SetTitleAction
  extends AppAction<
    ActionKey.SET_TITLE,
    { title: string; error: Error | null }
  > {}
interface SetSlugAction
  extends AppAction<ActionKey.SET_SLUG, { slug: string }> {}
interface SetKernelAction
  extends AppAction<ActionKey.SET_KERNEL, { id: number }> {}
export interface SetRunAction
  extends AppAction<ActionKey.SET_RUN, { id: number }> {}
export interface SetStartedCanceled
  extends AppAction<ActionKey.SET_STARTED_CANCELED, { isCanceled: boolean }> {}
export interface StopRunAction extends AppAction<ActionKey.STOP_RUN, {}> {}

interface SetDraftSpecAction
  extends AppAction<
    ActionKey.SET_DRAFT_SPEC,
    {
      id: number;
      entity: Kaggle.Sdk.Kernels.Models.KernelDraft | null;
      draftSpec: DraftSpec;
    }
  > {}
interface SetKernelSpecAction
  extends AppAction<ActionKey.SET_KERNEL_SPEC, KernelSpec> {}
interface SetCurrentLanguageIdAction
  extends AppAction<
    ActionKey.SET_CURRENT_LANGUAGE_ID,
    { languageId: LanguageId | null }
  > {}
interface SetDockerImagesAction
  extends AppAction<
    ActionKey.SET_DOCKER_IMAGES,
    { images: DockerImage[]; selectedVersion: DockerImagePinningType | number }
  > {}
interface SetDockerImageAction
  extends AppAction<ActionKey.SET_DOCKER_IMAGE, { imageId: number }> {}
interface SetDockerPinningTypeAction
  extends AppAction<
    ActionKey.SET_DOCKER_PINNING_TYPE,
    { pinningType: DockerImagePinningType }
  > {}
interface SetIsInternetEnabledAction
  extends AppAction<
    ActionKey.SET_IS_INTERNET_ENABLED,
    { isEnabled: boolean }
  > {}
interface SetIsInternetEnabledBackendAction
  extends AppAction<
    ActionKey.SET_IS_INTERNET_ENABLED_BACKEND,
    { isEnabled: boolean }
  > {}
interface SetBackendUnitName
  extends AppAction<ActionKey.SET_BACKEND_UNIT_NAME, { unitName: string }> {}
interface SetIsGpuEnabledAction
  extends AppAction<ActionKey.SET_IS_GPU_ENABLED, { isEnabled: boolean }> {}
interface SetAcceleratorAction
  extends AppAction<ActionKey.SET_ACCELERATOR, { accelerator: Accelerator }> {}
export interface SetSaveStatusSavedAction
  extends AppAction<ActionKey.SET_SAVE_STATUS_SAVED, { sequence: number }> {}
interface SetSaveStatusErrorAction
  extends AppAction<ActionKey.SET_SAVE_STATUS_ERROR, {}> {}
export interface SetJupyterProxyUrlAction
  extends AppAction<ActionKey.SET_JUPYTER_PROXY_URL, { url: string }> {}
interface SetDisableCommentsAction
  extends AppAction<
    ActionKey.SET_DISABLE_COMMENTS,
    { disableComments: boolean }
  > {}
interface SetPrivacyAction
  extends AppAction<ActionKey.SET_PRIVACY, { isPrivate: boolean }> {}

/** Trying an alternative to current source tracking (basically no tracking) */
interface SetIpynbAction
  extends AppAction<
    ActionKey.SET_IPYNB,
    { ipynb: string; immediate: boolean }
  > {}
interface SetDataSourcesAction
  extends AppAction<
    ActionKey.SET_DATA_SOURCES,
    { dataSources: DataSourceExtended[] }
  > {}
interface SetRemovedDataSourceAction
  extends AppAction<
    ActionKey.SET_REMOVED_DATA_SOURCE,
    { dataSource: LegacyDataSource | null }
  > {}  
interface SetDataSourceReferencesAction
  extends AppAction<
    ActionKey.SET_DATA_SOURCE_REFERENCES,
    { references: LegacyDataSourceReference[] }
  > {}
interface SetFirebaseSessionIdAction
  extends AppAction<
    ActionKey.SET_FIREBASE_SESSION_ID,
    { firebaseSessionId: string | null }
  > {}
interface SetQueuePositionAction
  extends AppAction<
    ActionKey.SET_QUEUE_POSITION,
    { position: number | null }
  > {}
interface SetKernelIdUrlAction
  extends AppAction<ActionKey.SET_KERNEL_ID_PATH, { kernelIdPath: string }> {}

interface SetDockerImageTagAction
  extends AppAction<
    ActionKey.SET_DOCKER_IMAGE_TAG,
    { dockerImageTag: string }
  > {}

interface SetWorkerPoolNameAction
  extends AppAction<
    ActionKey.SET_WORKER_POOL_NAME,
    { workerPoolName: string }
  > {}

interface SetCollaboratorsAction
  extends AppAction<
    ActionKey.SET_COLLABORATORS,
    { collaborators: Kaggle.Sdk.Users.Models.Collaborator[] }
  > {}
interface SetOwnerAction
  extends AppAction<
    ActionKey.SET_OWNER,
    { owner: Kaggle.Sdk.Users.Models.Collaborator }
  > {}

interface SetTagsAction
  extends AppAction<ActionKey.SET_TAGS, { tags: Tag[] }> {}

interface SetShowAcceleratorUnusedCalloutAction
  extends AppAction<
    ActionKey.SET_SHOW_ACCELERATOR_UNUSED_CALLOUT,
    { show: boolean }
  > {}

interface SetForkedKernelUserPlanAction
  extends AppAction<
    ActionKey.SET_SHOW_KERNEL_FORKED_FROM_PAID_TIER_WARNING,
    { show: boolean }
  > {}

// TODO(cochamberlain): Rename this to KernelAction.
export type NotebookAction =
  | SetTitleAction
  | SetSlugAction
  | SetKernelAction
  | SetRunAction
  | StopRunAction
  | SetCurrentLanguageIdAction
  | SetDockerImagesAction
  | SetIsInternetEnabledAction
  | SetIsInternetEnabledBackendAction
  | SetBackendUnitName
  | SetIsGpuEnabledAction
  | SetAcceleratorAction
  | SetSaveStatusSavedAction
  | SetSaveStatusErrorAction
  | SetJupyterProxyUrlAction
  | BatchedRunAction
  | SetDisableCommentsAction
  | SetPrivacyAction
  | SetIpynbAction
  | SetDataSourcesAction
  | SetRemovedDataSourceAction
  | SetDataSourceReferencesAction
  | SetFirebaseSessionIdAction
  | SetQueuePositionAction
  | SetKernelIdUrlAction
  | SetDockerImageTagAction
  | SetWorkerPoolNameAction
  | SetCollaboratorsAction
  | SetOwnerAction
  | SetDraftSpecAction
  | SetKernelSpecAction
  | SetDockerImageAction
  | SetDockerPinningTypeAction
  | SetTagsAction
  | SetShowAcceleratorUnusedCalloutAction
  | SetForkedKernelUserPlanAction;

export const setKernelSpec = (kernelSpec: KernelSpec) =>
  getStore().dispatch({
    type: ActionKey.SET_KERNEL_SPEC,
    payload: kernelSpec,
  });

export const setKernel = (id: number): SetKernelAction =>
  getStore().dispatch({
    type: ActionKey.SET_KERNEL,
    payload: { id },
  });

export const setRun = (id: number): SetRunAction =>
  getStore().dispatch({
    type: ActionKey.SET_RUN,
    payload: { id },
  });

export const setStartedCanceled = (isCanceled: boolean): SetStartedCanceled =>
  getStore().dispatch({
    type: ActionKey.SET_STARTED_CANCELED,
    payload: { isCanceled },
  });

export const stopRun = (): StopRunAction =>
  getStore().dispatch({
    type: ActionKey.STOP_RUN,
    payload: {},
  });

export const setCurrentLanguageId = (
  languageId: LanguageId | null
): SetCurrentLanguageIdAction =>
  getStore().dispatch({
    type: ActionKey.SET_CURRENT_LANGUAGE_ID,
    payload: { languageId },
  });

export const setDraftSpec = (
  id: number,
  entity: Kaggle.Sdk.Kernels.Models.KernelDraft | null,
  draftSpec: DraftSpec
): SetDraftSpecAction =>
  getStore().dispatch({
    type: ActionKey.SET_DRAFT_SPEC,
    payload: { id, entity, draftSpec },
  });

export const setDockerImages = (
  images: DockerImage[],
  selectedVersion: DockerImagePinningType | number
): SetDockerImagesAction =>
  getStore().dispatch({
    type: ActionKey.SET_DOCKER_IMAGES,
    payload: {
      images,
      selectedVersion,
    },
  });

export const setDockerImage = (imageId: number): SetDockerImageAction =>
  getStore().dispatch({
    type: ActionKey.SET_DOCKER_IMAGE,
    payload: {
      imageId,
    },
  });

export const setDockerImageTag = (
  dockerImageTag: string
): SetDockerImageTagAction =>
  getStore().dispatch({
    type: ActionKey.SET_DOCKER_IMAGE_TAG,
    payload: {
      dockerImageTag,
    },
  });

export const setDockerPinningType = (
  pinningType: DockerImagePinningType
): SetDockerPinningTypeAction =>
  getStore().dispatch({
    type: ActionKey.SET_DOCKER_PINNING_TYPE,
    payload: {
      pinningType,
    },
  });

/** Sets when the internet setting is toggled */
export const setIsInternetEnabled = (
  isEnabled: boolean
): SetIsInternetEnabledAction =>
  getStore().dispatch({
    type: ActionKey.SET_IS_INTERNET_ENABLED,
    payload: { isEnabled },
  });

/** Sets when the internet setting is changed in the backend */
export const setIsInternetEnabledBackend = (
  isEnabled: boolean
): SetIsInternetEnabledBackendAction =>
  getStore().dispatch({
    type: ActionKey.SET_IS_INTERNET_ENABLED_BACKEND,
    payload: { isEnabled },
  });

export const setBackendUnitName = (unitName: string): SetBackendUnitName =>
  getStore().dispatch({
    type: ActionKey.SET_BACKEND_UNIT_NAME,
    payload: { unitName },
  });

export const setIsGpuEnabled = (isEnabled: boolean): SetIsGpuEnabledAction =>
  getStore().dispatch({
    type: ActionKey.SET_IS_GPU_ENABLED,
    payload: { isEnabled },
  });

export const setAccelerator = (
  accelerator: Accelerator
): SetAcceleratorAction =>
  getStore().dispatch({
    type: ActionKey.SET_ACCELERATOR,
    payload: { accelerator },
  });

export const setSaveStatusToSaved = (
  sequence: number
): SetSaveStatusSavedAction =>
  getStore().dispatch({
    type: ActionKey.SET_SAVE_STATUS_SAVED,
    payload: { sequence },
  });

export const setSaveStatusToError = (): SetSaveStatusErrorAction =>
  getStore().dispatch({
    type: ActionKey.SET_SAVE_STATUS_ERROR,
    payload: {},
  });

export const setDisableComments = (
  disableComments: boolean
): SetDisableCommentsAction =>
  getStore().dispatch({
    type: ActionKey.SET_DISABLE_COMMENTS,
    payload: { disableComments },
  });

export const setPrivacy = (isPrivate: boolean): SetPrivacyAction =>
  getStore().dispatch({
    type: ActionKey.SET_PRIVACY,
    payload: { isPrivate },
  });

/** Sets the url of the Jupyter proxy. */
export const setJupyterProxyUrl = (url: string): SetJupyterProxyUrlAction =>
  getStore().dispatch({
    type: ActionKey.SET_JUPYTER_PROXY_URL,
    payload: { url },
  });

let lastSetIpynbMS = Date.now();

export const setIpynb = (ipynb: string, immediate = false): SetIpynbAction => {
  lastSetIpynbMS = Date.now();
  return getStore().dispatch({
    type: ActionKey.SET_IPYNB,
    payload: { ipynb, immediate },
  });
};

let ipynbTimeoutId: number | undefined;
const IPYNB_TROTTLE_MS = 250;
const IPYNB_MAX_THROTTLE_MS = 5000;

// Used during uploads to bring AppState.ipynb into consistency
// with Jupyter's version as quickly as possible.
let immediateSaveIpynb = false;
export const setImmediateSaveIpynb = (val: boolean) => {
  immediateSaveIpynb = val;
}

export const saveIpynb = (ipynb: string) => {
  window.clearTimeout(ipynbTimeoutId);
  // Calculate time from the last setting of IPYNB data in state. If it surpasses the max time, trigger set immediately.
  // Ensures that when the user is constantly typing, saves still go through.
  const elapsedMS = Date.now() - lastSetIpynbMS;
  if (immediateSaveIpynb || elapsedMS >= IPYNB_MAX_THROTTLE_MS) {
    setIpynb(ipynb, true);
  } else {
    ipynbTimeoutId = window.setTimeout(() => {
      setIpynb(ipynb);
    }, IPYNB_TROTTLE_MS);
  }
};

export const setDataSources = (
  dataSources: DataSourceExtended[]
): SetDataSourcesAction =>
  getStore().dispatch({
    type: ActionKey.SET_DATA_SOURCES,
    payload: { dataSources },
  });

export const setRemovedDataSource = (
  dataSource: LegacyDataSource | null 
): SetRemovedDataSourceAction =>
  getStore().dispatch({
    type: ActionKey.SET_REMOVED_DATA_SOURCE,
    payload: { dataSource },
  });

export const setDataSourceReferences = (
  references: LegacyDataSourceReference[]
): SetDataSourceReferencesAction =>
  getStore().dispatch({
    type: ActionKey.SET_DATA_SOURCE_REFERENCES,
    payload: { references },
  });

export const setFirebaseSessionId = (
  firebaseSessionId: string | null
): SetFirebaseSessionIdAction =>
  getStore().dispatch({
    type: ActionKey.SET_FIREBASE_SESSION_ID,
    payload: { firebaseSessionId },
  });

export const setQueuePosition = (
  position: number | null
): SetQueuePositionAction =>
  getStore().dispatch({
    type: ActionKey.SET_QUEUE_POSITION,
    payload: { position },
  });

export const setKernelIdUrl = (kernelIdPath: string): SetKernelIdUrlAction =>
  getStore().dispatch({
    type: ActionKey.SET_KERNEL_ID_PATH,
    payload: { kernelIdPath },
  });

export const setWorkerPoolName = (
  workerPoolName: string
): SetWorkerPoolNameAction =>
  getStore().dispatch({
    type: ActionKey.SET_WORKER_POOL_NAME,
    payload: { workerPoolName },
  });

export const setTitle = (
  title: string,
  error: Error | null = null
): SetTitleAction =>
  getStore().dispatch({
    type: ActionKey.SET_TITLE,
    payload: { title, error },
  });

export const setSlug = (slug: string): SetSlugAction =>
  getStore().dispatch({
    type: ActionKey.SET_SLUG,
    payload: { slug },
  });

export const updateTitle = async (
  newTitle: string
): Promise<string | Error> => {
  const services = getServices();
  const state = getStore().getState();
  const kernelRunId = state.notebook.run;
  if (kernelRunId === null) {
    throw new Error("Cannot update title for null kernelRunId.");
  }
  const result = await services.kernelsClient.updateKernelTitle({
    newTitle,
    scriptVersionId: kernelRunId,
  });
  if (result.errorMessage) {
    const error = new Error(result.errorMessage);
    setTitle(newTitle, error);
    return error;
  } else {
    setTitle(newTitle);
    if (result.slug) {
      setSlug(result.slug);
    }
    appendTerminal([
      new TerminalMessage(`Kernel title updated to ${newTitle}`),
    ]);

    // Updates current kernel id URL and return the navigation path.
    const kernelIdUrl = `/${result.username}/${result.slug}`;
    setKernelIdUrl(kernelIdUrl);
    return kernelIdUrl;
  }
};

export const setCollaborators = (
  collaborators: Kaggle.Sdk.Users.Models.Collaborator[]
): SetCollaboratorsAction =>
  getStore().dispatch({
    type: ActionKey.SET_COLLABORATORS,
    payload: { collaborators },
  });

export const setOwner = (
  owner: Kaggle.Sdk.Users.Models.Collaborator
): SetOwnerAction =>
  getStore().dispatch({
    type: ActionKey.SET_OWNER,
    payload: { owner },
  });

export const showAcceleratorUnusedCallout = (
  show: boolean
): SetShowAcceleratorUnusedCalloutAction =>
  getStore().dispatch({
    type: ActionKey.SET_SHOW_ACCELERATOR_UNUSED_CALLOUT,
    payload: { show },
  });

/** Sets the tags associated with the kernel. */
export const setTags = (tags: Tag[]): SetTagsAction =>
  getStore().dispatch({
    type: ActionKey.SET_TAGS,
    payload: { tags },
  });

/** Updates category ids from the database using latest redux kernel id. */
export const fetchTags = async () => {
  const services = getServices();

  const kernelId = getStore().getState().notebook.kernel;
  if (kernelId === null) {
    throw new Error("Cannot fetch categories with null kernelId.");
  }

  const response = await services.kernelsClient.getKernelCategoryIds({
    kernelId,
  });

  const tags = await services.tagsClient.getTagsById({
    tagIds: response.categoryIds,
  });
  setTags(tags.tags);
};

// Legacy 'categoryId' method only used to add the UtilityScript categoryId
const addCategoryId = async (categoryId: number) => {
  const kernelId = getStore().getState().notebook.kernel;
  if (kernelId === null) {
    throw new Error("Cannot set categories with null kernelId.");
  }

  await getServices().kernelsClient.addKernelCategory({
    kernelId,
    categoryId,
  });
  await fetchTags();
};

// Legacy 'categoryId' method only used to remove the UtilityScript categoryId
const removeCategoryId = async (categoryId: number) => {
  const kernelId = getStore().getState().notebook.kernel;
  if (kernelId === null) {
    throw new Error("Cannot set categories with null kernelId.");
  }

  await getServices().kernelsClient.removeKernelCategory({
    kernelId,
    categoryId,
  });
  await fetchTags();
};

/** Turn the kernel into a utility script. */
export const setUtilityScript = async () => {
  const state = getStore().getState();
  if (getIsUtilityScript(state)) {
    throw new Error("The kernel is already a utility script.");
  }

  await addCategoryId(UTILITY_SCRIPT_CATEGORY_ID);
};

/** Turn the kernel into a non-utility script. */
export const unsetUtilityScript = async () => {
  const state = getStore().getState();
  if (!getIsUtilityScript(state)) {
    throw new Error("The kernel is not a utility script.");
  }
  return removeCategoryId(UTILITY_SCRIPT_CATEGORY_ID);
};

/** Change GPU status (enabled, disabled) for a given kernel. */
const toggleGpu = async (isEnabled: boolean) => {
  setIsGpuEnabled(isEnabled);
  await restartWorker();
};

/** Enables GPU for the provided kernel */
export const enableGpu = async () => {
  await toggleGpu(true /* isEnabled */);
};

/** Disables GPU for the provided kernel */
export const disableGpu = async () => {
  toggleGpu(false /* isEnabled */);
};

/** Updates Accelerator type for notebook */
export const updateAccelerator = async (accelerator: Accelerator) => {
  const state = getStore().getState();
  const currentAccelerator = getAccelerator(state);

  if (currentAccelerator !== accelerator) {
    setAccelerator(accelerator);
    if (isTpuAccelerator(accelerator)) {
      setIsInternetEnabled(true);
    }
    await restartWorker();
  }
};

/** Change internet status (enabled, disabled) for a given kernel. */
export const toggleInternet = async (isEnabled: boolean) => {
  const state = getStore().getState();
  const isInternetEnabled = state.notebook.internet;
  if (isInternetEnabled !== isEnabled) {
    setIsInternetEnabled(isEnabled);
    await updateWorker({updateInternet: true});
  }
};

/** Enables internet for the provided kernel */
export const enableInternet = () => toggleInternet(true /* isEnabled */);

/** Disables internet for the provided kernel */
export const disableInternet = () => toggleInternet(false /* isEnabled */);

/** Set a warning banner if there's a tier mismatch */
export const setShowKernelForkedFromPaidTierWarning = (show: boolean) => {
  const { dispatch } = getStore();
  dispatch({
    type: ActionKey.SET_SHOW_KERNEL_FORKED_FROM_PAID_TIER_WARNING,
    payload: { show },
  });
};

export const commitAndRun = async (
  source: JupyterSource,
  versionName: string | null,
  versionType: KernelVersionType,
  saveQuickVersionOutputs: boolean,
  accelerator: Accelerator,
  userPlanOverride: Kaggle.Sdk.Users.Protos.UserPlan | null
) => {
  const services = getServices();

  // Set to comitting state while request is sent.
  setBatchedRunCommitting();

  try {
    // Wait for any inflight autosaves.
    await draftSequenceMutex.acquire();

    // Obtain state *after* we aquire the sequence mutex, otherwise we may
    // have read the sequence number while unprotected, and it may have changed!
    const state = getStore().getState();

    const kernelId = state.notebook.kernel;
    if (!kernelId) {
      throw new Error("Cannot commit with null kernel id.");
    }

    // Need to convert proto values back to C# enum values, somehow transpiler generated
    // different enums here.
    const requestUserPlanOverride =
      userPlanOverride === Kaggle.Sdk.Users.Protos.UserPlan.FREE
        ? "free"
        : Kaggle.Sdk.Users.Protos.UserPlan.KAGGLE_PRO
        ? "kagglePro"
        : null;

    const response = await services.kernelsClient.commitAndRun({
      dataSources: getCurrentDataSourceReferences(state),
      dockerImageTag: getCurrentDockerImageTag(state),
      forkParentScriptVersionId: null,
      isLanguageTemplate: false,
      newText: featureFlags.kernelsSaveCellOutput ? source.serialize() : source.stripAndSerialize(),
      newTitle: state.notebook.title,
      scriptId: kernelId,
      scriptLanguageName: getCurrentLanguageName(state),
      dockerImageVersionId: null,
      isPrivate: state.notebook.isPrivate,
      editorType: getSourceType(state),
      workerPoolName: state.notebook.workerPoolName,
      sequence: state.notebook.sequence,
      compute: {
        accelerator,
        internet: { isEnabled: state.notebook.internet },
        constraints: null,
      },
      kernelVersionId: null,
      versionName,
      versionType,
      userPlanOverride: requestUserPlanOverride,
    });

    if (response.sequence === undefined || response.sequence === null) {
      throw new Error("Commit & Run response has no sequence param.");
    }

    if (response.status === "error") {
      // There was an error in the commit, but the interactive session is
      // still valid, and the sequence number may have changed.
      setSaveStatusToSaved(response.sequence);
      throw new Error(response.message || "Unknown error. Please try again.");
    }

    if (versionType === "quick") {
      if (response.blobUploadUrl === null) {
        throw new Error("Failed to upload notebook source. Please try again.");
      }

      // Upload the blob to GCS; use __notebook__.ipynb for all sources
      // including {r, python} and {notebooks, scripts}.
      await putGcsBlob(
        response.blobUploadUrl,
        new File([source.serialize()], "__notebook__.ipynb", undefined),
        "application/octet-stream",
        true, // attemptResume
        (progressInfo: UploadProgress) => {
          if (!progressInfo.hasStarted) {
            setBatchedRunUploading(0);
          }
          const progress = progressInfo.totalBytesLoaded;
          setBatchedRunUploading(progress);
        },
        new CancellationToken()
      );

      if (saveQuickVersionOutputs) {
        setBatchedRunUploadingFiles();
        await services.kernelsClient.uploadKernelSessionOutput({
          interactiveSessionId: getCurrentKernelRunId(state) || 0,
          batchSessionId: response.kernelRunId,
        });
      }

      // Start the html conversion process
      const finalizeResponse = await services.kernelsClient.completeQuickSession(
        {
          kernelSessionId: response.kernelRunId,
          workerPoolName: state.notebook.workerPoolName,
        }
      );

      if (finalizeResponse.errorMessage !== null) {
        throw new Error("Failed to start notebook session. Please try again.");
      }
    }

    fetchRunLogs(response.kernelRunId);

    await watchRunForStatus(
      response.kernelRunId,
      response.sequence,
      versionType,
      accelerator
    );
  } catch (error) {
    removeBatchedRun();
    const errorMessages = normalizeAmbiguousErrorMessages(error);
    const singleError = errorMessages.length > 0 ? errorMessages[0] : "";
    if (featureFlags.kernelsQuotaModals && handleQuotaError(singleError)) {
      // Quota error handled, do nothing.
    } else {
      const message = `An error occurred while committing kernel: ${singleError}`;
      setBatchedRunErrorMessage(message);
      appendTerminalError(
        "An error occurred while committing the kernel.",
        error
      );
    }
  } finally {
    draftSequenceMutex.release();
  }
};
